import React, { useEffect, useRef, useState } from "react";

import { CaretDown, X } from "phosphor-react";

import { useDebounce, useOutsideClick } from "../../Hooks"

const getPositionFromBotton = (sectionId, dropdownButton, dropdownList) => {
    const section = document.getElementById(sectionId)
    const buttonRect = dropdownButton.getBoundingClientRect();
    const dropdownHeight = dropdownList.offsetHeight;
    const containerRect = section.getBoundingClientRect();

    const spaceBelow = containerRect.bottom - buttonRect.bottom;

    return spaceBelow
}

//TODO: Trigger for field validation
//TODO: add field validation and errors
//TODO: add info tooltip
const SingleSelectDropdown = ({
    fieldType,
    valueConfig,
    displayAttributes,
    onChangeCallback,
    onErrorCallback,
    dropDownClassName='hover:bg-accent rounded-md w-full',
    ...additionalProps
}) => {
    const [selectedValue, setSelectedValue] = useState(null); //initialize value with selectedValues(previously entered value)
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState("");
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const { possibleValues, selectedValues: prevSelectedValues, validation: { mandatoryField } } = valueConfig;
    const { displayName, disabled } = displayAttributes;
    const { sectionId, formSubmitClick, viewMode } = additionalProps || {}
    const errorConfig = {
        mandatoryField: "Field cannot be empty"
    }

    const ref = useRef()
    const dropdownButtonRef = useRef()
    const dropdownListRef = useRef()

    //for view mode, to preselect previous value
    useEffect(() => {
        if (prevSelectedValues && prevSelectedValues.length) {
            //prevSelectedValues has the list of item ids stored
            const selectedId = prevSelectedValues[0]?.toLowerCase()
            const selectedDropdownItem = possibleValues?.find(({ id }) => id?.toLowerCase() === selectedId)
            setSelectedValue(selectedDropdownItem)
        }
    }, [prevSelectedValues])

    useOutsideClick(ref, () => {
        setDropdownOpen(false)
    })

    useEffect(() => {
        //validate the input value and show error, and also send back error true to parent via a callback
        if (formSubmitClick) {
            if (mandatoryField) {
                //check if the field has been selected or not
                if (!selectedValue) {
                    onErrorCallback(true)
                    setShowError(true)
                    setErrorMessage(errorConfig.mandatoryField)
                } else {
                    onErrorCallback(false)
                    setShowError(false)
                    setErrorMessage("")
                }
            }
        }

    }, [formSubmitClick])

    const getTransformedPayload = useDebounce((selectedValue) => {
        const payload = {
            fieldType,
            valueConfig: {
                ...valueConfig,
                selectedValues: [selectedValue.id]
            },
            displayAttributes
        }

        onChangeCallback(payload)
    }, 300)

    const handleClear = () => {
        setSelectedValue(null);
        delete valueConfig?.selectedValues;

        const payload = {
            fieldType,
            valueConfig,
            displayAttributes
        }
        onChangeCallback(payload);
    }

    return (
        <div className="flex flex-col gap-1">
            <p className="text-lg">{displayName}
                {mandatoryField && <span className="text-base font-semibold text-red-400">*</span>}
            </p>
            <div className={`dropdown w-max ${dropdownPosition === 'top' ? "dropdown-top" : ""} ${dropdownOpen ? "dropdown-open" : ""}`} ref={ref}>
                {
                    viewMode ? <p className="text-sm text-secondary">{selectedValue?.item}</p>
                        :
                        <>
                            <div
                                role="button"
                                className={`
                                btn m-0 no-animation min-w-72 justify-between bg-accent hover:bg-secondary 
                                ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
                                ref={dropdownButtonRef}
                                onClick={(evt) => {
                                    evt?.stopPropagation()
                                    if (disabled) {
                                        evt?.preventDefault()
                                    } else {
                                        setDropdownOpen(prev => !prev)
                                        if (dropdownButtonRef.current && dropdownListRef.current && sectionId) {
                                            const spaceBelow = getPositionFromBotton(sectionId, dropdownButtonRef.current, dropdownListRef.current)
                                            const dropdownHeight = dropdownListRef.current.offsetHeight;
                                            if (spaceBelow < dropdownHeight) {
                                                // ref.current.classList.add("dropdown-top")
                                                setDropdownPosition("top")
                                            }
                                        }
                                    }
                                }}
                            >
                                {selectedValue ? (
                                    <div
                                        className="flex justify-between items-center w-full"
                                    >
                                        {selectedValue?.item}
                                        {
                                            !disabled && (
                                                <span className="text-sm font-normal" onClick={(evt) => {
                                                    evt.stopPropagation();
                                                    handleClear();
                                                }}
                                                >
                                                    Clear
                                                </span>
                                            )
                                        }

                                    </div>
                                ) : (
                                    <>
                                        <span>Please select a {displayName}</span>
                                        <CaretDown className={`font-semibold transition-all duration-100 ${dropdownOpen ? "rotate-180" : ""}`} weight="bold" size={16} />
                                    </>
                                )}
                            </div>
                            <ul className="dropdown-content my-2 w-full z-[2] menu p-2 flex-col flex-nowrap shadow bg-base-100 border border-gray-300 rounded-box max-h-44 overflow-auto" ref={dropdownListRef}>
                                {
                                    possibleValues?.map(
                                        ({ item, id, ..._keys }, index) =>
                                            <li key={id} onClick={(evt) => {
                                                evt?.stopPropagation()
                                                setSelectedValue({ item, id, ..._keys })
                                                setDropdownOpen(false)
                                                getTransformedPayload({ item, id, ..._keys }) // should be pass the final payload
                                            }} className={dropDownClassName}><a>{item}</a></li>
                                    )
                                }
                            </ul>
                        </>
                }
            </div>
            {
                showError && <p className="text-base text-red-400">{errorMessage}</p>
            }
        </div>
    )
}

const MultiSelectDropdown = ({
    fieldType,
    valueConfig,
    displayAttributes,
    onChangeCallback,
    onErrorCallback,
    ...additionalProps
}) => {
    const [selectedValues, setSelectedValues] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState("");
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const { displayName, placeholderText } = displayAttributes;
    const { possibleValues, selectedValues: prevSelectedValues, validation: { mandatoryField } } = valueConfig;
    const { sectionId, formSubmitClick, viewMode } = additionalProps || {}
    const errorConfig = {
        mandatoryField: "Field cannot be empty"
    }

    const ref = useRef()
    const dropdownButtonRef = useRef()
    const dropdownListRef = useRef()
    useOutsideClick(ref, () => {
        setDropdownOpen(false)
    })

    useEffect(() => {
        //validate the input value and show error, and also send back error true to parent via a callback
        if (formSubmitClick) {
            if (mandatoryField) {
                //check if the field has been selected or not
                if (!selectedValues) {
                    onErrorCallback(true)
                    setShowError(true)
                    setErrorMessage(errorConfig.mandatoryField)
                } else {
                    onErrorCallback(false)
                    setShowError(false)
                    setErrorMessage("")
                }
            }
        }

    }, [formSubmitClick])

    useEffect(() => {
        //for view mode, to preselect previous value
        if (prevSelectedValues && prevSelectedValues.length) {
            //prevSelectedValues has the list of item ids stored
            const selectedIds = prevSelectedValues
            const selectedDropdownItems = possibleValues.filter(({ id }) => selectedIds.includes(id))
            setSelectedValues(selectedDropdownItems)
        }
    }, [prevSelectedValues])

    const getTransformedPayload = useDebounce((selectedValue) => {
        const payload = {
            fieldType,
            valueConfig: {
                ...valueConfig,
                selectedValues: selectedValue
            },
            displayAttributes
        }

        onChangeCallback(payload)
    }, 300)

    const onChangeHandler = ({ item, id, ..._keys }) => {
        //selected values is an object with keys is id and values as item
        //when null or object is empty
        if (!selectedValues || !Object.keys(selectedValues).length) {
            setSelectedValues({
                [id]: {
                    item,
                    ..._keys
                }
            })
            //send data to parent component
            getTransformedPayload([id])
        } else {
            //Change the below toggling id logic
            const ogSelectedValues = { ...selectedValues }
            //if Object has values, then toggle if previously selected or else add it
            if (selectedValues[id]) {
                delete ogSelectedValues[id]
                setSelectedValues(ogSelectedValues)
            } else {
                ogSelectedValues[id] = {
                    item,
                    ..._keys
                }
                setSelectedValues(ogSelectedValues)
            }
            const selectedValuesPayload = Object.entries(ogSelectedValues || {}).map(([key, value]) => (key))
            //send data to parent component
            getTransformedPayload(selectedValuesPayload)
        }
    }

    const getSelectedValuesName = () => {
        const labelValue = Object.values(selectedValues || {}).map(({ item }) => item).join(", ")
        return labelValue
    }

    return (
        <div className="flex flex-col gap-1">
            <p className="text-lg">{displayName}
                {mandatoryField && <span className="text-base font-semibold text-red-400">*</span>}
            </p>
            {
                viewMode ?
                    <p className="text-sm text-secondary">{getSelectedValuesName()}</p>
                    :
                    <>
                        <div className={`dropdown w-max ${dropdownPosition === 'top' ? "dropdown-top" : ""} ${dropdownOpen ? "dropdown-open" : ""}`} ref={ref}>
                            <div role="button" className="btn m-0 no-animation min-w-72 w-max flex justify-between bg-accent hover:bg-secondary" ref={dropdownButtonRef} onClick={(evt) => {
                                evt?.stopPropagation()
                                setDropdownOpen(prev => !prev)
                                if (dropdownButtonRef.current && dropdownListRef.current && sectionId) {
                                    const spaceBelow = getPositionFromBotton(sectionId, dropdownButtonRef.current, dropdownListRef.current)
                                    const dropdownHeight = dropdownListRef.current.offsetHeight;
                                    if (spaceBelow < dropdownHeight) {
                                        // ref.current.classList.add("dropdown-top")
                                        setDropdownPosition("top")
                                    }
                                }
                            }}>
                                <p className="w-[85%] text-left text-ellipsis overflow-hidden">
                                    {selectedValues && Object.keys(selectedValues).length ? getSelectedValuesName() : `Please select a ${displayName}`}
                                </p>
                                <CaretDown className={`font-semibold transition-all duration-100 ${dropdownOpen ? "rotate-180" : ""}`} weight="bold" size={16} />
                            </div>
                            <ul className="dropdown-content my-2 w-full z-[1] menu p-2 flex-col flex-nowrap border border-gray-300 shadow bg-base-100 rounded-box max-h-44 overflow-scroll" ref={dropdownListRef}>
                                {
                                    possibleValues?.map(
                                        ({ item, id, ..._keys }, index) =>
                                            <li key={id} onClick={(evt) => {
                                                evt?.stopPropagation()
                                                onChangeHandler({ item, id, ..._keys })
                                            }}
                                                className="hover:bg-accent flex flex-row w-full gap-4 items-center p-2 rounded-md cursor-pointer"
                                            >
                                                <label className="label p-0 accent-label">
                                                    <input type="checkbox" checked={selectedValues ? selectedValues[id] ? true : false : false} onChange={(evt) => {
                                                        evt?.stopPropagation()
                                                        onChangeHandler({ item, id, ..._keys })
                                                    }} className="checkbox checkbox-md checkbox-primary" />
                                                    <a className="w-max hover:bg-accent" onClick={(evt) => {
                                                        evt.preventDefault()
                                                    }}>{item}</a>
                                                </label>
                                            </li>
                                    )
                                }
                            </ul>
                        </div>
                        {
                            showError && <p className="text-base text-red-400">{errorMessage}</p>
                        }
                    </>
            }
        </div>
    )
}

const TextArea = ({
    fieldType,
    valueConfig,
    displayAttributes,
    onChangeCallback,
    onErrorCallback,
    ...additionalProps
}) => {
    const [value, setValue] = useState("") //initialize value with selectedValues(previously entered value)
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const { displayName, placeholderText } = displayAttributes;
    const { sectionId, formSubmitClick, viewMode } = additionalProps || {}
    const { validation: { mandatoryField }, selectedValues: prevSelectedValues } = valueConfig;
    const errorConfig = {
        mandatoryField: "Field cannot be empty"
    }

    useEffect(() => {
        if (prevSelectedValues) {
            setValue(prevSelectedValues[0])
        }
    }, [prevSelectedValues])

    useEffect(() => {
        //validate the input value and show error, and also send back error true to parent via a callback
        if (formSubmitClick) {
            if (mandatoryField) {
                //check if the field has been selected or not
                if (!value) {
                    onErrorCallback(true)
                    setShowError(true)
                    setErrorMessage(errorConfig.mandatoryField)
                } else {
                    onErrorCallback(false)
                    setShowError(false)
                    setErrorMessage("")
                }
            }
        }

    }, [formSubmitClick])

    const getTransformedPayload = useDebounce((selectedValue) => {
        const payload = {
            fieldType,
            valueConfig: {
                ...valueConfig,
                selectedValues: [selectedValue]
            },
            displayAttributes
        }

        onChangeCallback(payload)
    }, 300)

    return (
        <div className="flex flex-col gap-1">
            <p className="text-lg">{displayName}
                {mandatoryField && <span className="text-base font-semibold text-red-400">*</span>}
            </p>
            {
                viewMode ? <p className="text-sm text-secondary">{value}</p>
                    :
                    <>
                        <textarea
                            className="textarea textarea-bordered w-1/2 resize-none"
                            placeholder={placeholderText ? placeholderText : `Please Enter ${displayName}`}
                            value={value}
                            onChange={(evt) => {
                                setValue(evt.target.value)
                                //debounce and update in parent field
                                getTransformedPayload(evt.target.value)
                            }}
                        ></textarea>
                        {
                            showError && <p className="text-base text-red-400">{errorMessage}</p>
                        }
                    </>

            }
        </div>
    )
}

const TextInput = ({
    fieldType,
    valueConfig,
    displayAttributes,
    onChangeCallback,
    onErrorCallback,
    ...additionalProps
}) => {
    const [value, setValue] = useState("") //initialize value with selectedValues(previously entered value)
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const { displayName, placeholderText, disabled } = displayAttributes;
    const { sectionId, formSubmitClick, viewMode } = additionalProps || {}
    const { validation: { mandatoryField }, selectedValues: prevSelectedValues } = valueConfig;
    const errorConfig = {
        mandatoryField: "Field cannot be empty"
    }

    useEffect(() => {
        if (prevSelectedValues) {
            setValue(prevSelectedValues[0])
        }
    }, [prevSelectedValues])

    useEffect(() => {
        //validate the input value and show error, and also send back error true to parent via a callback
        if (formSubmitClick) {
            if (mandatoryField) {
                //check if the field has been selected or not
                if (!value) {
                    onErrorCallback(true)
                    setShowError(true)
                    setErrorMessage(errorConfig.mandatoryField)
                } else {
                    onErrorCallback(false)
                    setShowError(false)
                    setErrorMessage("")
                }
            }
        }

    }, [formSubmitClick])

    const getTransformedPayload = useDebounce((selectedValue) => {
        const payload = {
            fieldType,
            valueConfig: {
                ...valueConfig,
                selectedValues: [selectedValue]
            },
            displayAttributes
        }

        onChangeCallback(payload)
    }, 300)

    return (
        <div className="flex flex-col gap-1">
            <p className="text-lg">{displayName}
                {mandatoryField && <span className="text-base font-semibold text-red-400">*</span>}
            </p>
            {
                viewMode ? <p className="text-sm text-secondary">{value}</p>
                    :
                    <>
                        <input 
                            type={displayAttributes.type || "text"}
                            className={`input input-bordered w-full ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
                            placeholder={placeholderText ? placeholderText : `Please Enter ${displayName}`}
                            value={value}
                            disabled={disabled}
                            autoComplete={displayAttributes.autoComplete}
                            name={displayAttributes.name}
                            onChange={(evt) => {
                                setValue(evt.target.value)
                                //debounce and update in parent field
                                getTransformedPayload(evt.target.value)
                            }}
                        ></input>
                        {
                            showError && <p className="text-base text-red-400">{errorMessage}</p>
                        }
                    </>

            }
        </div>
    )
}


const MultiSelectCheckBox = ({
    fieldType,
    valueConfig,
    displayAttributes,
    onChangeCallback,
    onErrorCallback,
    ...additionalProps
}) => {
    const [selectedValues, setSelectedValues] = useState(null);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const { displayName } = displayAttributes;
    const { possibleValues, validation: { mandatoryField }, selectedValues: prevSelectedValues } = valueConfig;
    const { sectionId, formSubmitClick } = additionalProps || {}
    const errorConfig = {
        mandatoryField: "Field cannot be empty"
    }

    useEffect(() => {
        //for view mode, to preselect previous value
        if (prevSelectedValues && prevSelectedValues.length) {
            //prevSelectedValues has the list of item ids stored
            const selectedIds = prevSelectedValues
            const selectedOptions = possibleValues.filter(({ id }) => selectedIds.includes(id)).reduce((acc, curr) => ({
                ...acc,
                [curr.id]: {
                    item: curr.item
                }
            }), {})
            setSelectedValues(selectedOptions)
        }
    }, [prevSelectedValues])

    useEffect(() => {
        //validate the input value and show error, and also send back error true to parent via a callback
        if (formSubmitClick) {
            if (mandatoryField) {
                //check if the field has been selected or not
                if (!selectedValues) {
                    onErrorCallback(true)
                    setShowError(true)
                    setErrorMessage(errorConfig.mandatoryField)
                } else {
                    onErrorCallback(false)
                    setShowError(false)
                    setErrorMessage("")
                }
            }
        }

    }, [formSubmitClick])

    const getTransformedPayload = useDebounce((selectedValue) => {
        const payload = {
            fieldType,
            valueConfig: {
                ...valueConfig,
                selectedValues: selectedValue
            },
            displayAttributes
        }

        onChangeCallback(payload)
    }, 300)

    const onChangeHandler = ({ item, id, ..._keys }) => {
        //selected values is an object with keys is id and values as item
        //when null or object is empty
        if (!selectedValues || !Object.keys(selectedValues).length) {
            setSelectedValues({
                [id]: {
                    item,
                    ..._keys
                }
            })
            //send data to parent component
            getTransformedPayload([id])
        } else {
            const ogSelectedValues = { ...selectedValues }
            //if Object has values, then toggle if previously selected or else add it
            if (selectedValues[id]) {
                delete ogSelectedValues[id]
                setSelectedValues(ogSelectedValues)
            } else {
                ogSelectedValues[id] = {
                    item,
                    ..._keys
                }
                setSelectedValues(ogSelectedValues)
            }
            const selectedValuesPayload = Object.entries(ogSelectedValues || {}).map(([key, value]) => (key))
            //send data to parent component
            getTransformedPayload(selectedValuesPayload)
        }

    }

    return (
        <div className="flex flex-col gap-1">
            <p className="text-lg">{displayName}
                {mandatoryField && <span className="text-base font-semibold text-red-400">*</span>}
            </p>
            <div className="w-full flex flex-wrap gap-3">
                {
                    possibleValues?.map(
                        ({ item, id, ..._keys }, index) =>
                            <div key={id} onClick={(evt) => {
                                evt?.stopPropagation()
                                onChangeHandler({ item, id, ..._keys })
                                getTransformedPayload({ item, id, ..._keys }) // should be pass the final payload
                            }}
                                className="rounded-md cursor-pointer"
                            >
                                <label className="label p-0 accent-label flex gap-2 items-center">
                                    <input type="checkbox" checked={selectedValues ? selectedValues[id] ? true : false : false} onChange={(evt) => {
                                        evt?.stopPropagation()
                                        onChangeHandler({ item, id, ..._keys })
                                    }} className="checkbox checkbox-md checkbox-primary" />
                                    <a className="w-max" onClick={(evt) => {
                                        evt.preventDefault()
                                    }}>{item}</a>
                                </label>
                            </div>
                    )
                }
            </div>
            {
                showError && <p className="text-base text-red-400">{errorMessage}</p>
            }
        </div>
    )
}

/**
 * Till the below payload comes let's continue with the config json parsing
 * 
 */

//split the comma separated value into different values

const TextBox = ({
    fieldType,
    valueConfig,
    displayAttributes,
    operators,
    onChangeCallback,
    onErrorCallback,
    ...additionalProps
}) => {//can be added multiple times
    const [selectedValue, setSelectedValue] = useState([]); //get the previous data and split with comma or colon 
    //used for case 1, 2  
    const [operatorValue, setOperatorValue] = useState("");
    const [minValue, setMinValue] = useState("");
    const [maxValue, setMaxValue] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState("");
    const [activeSet, setActiveSet] = useState("");
    const [showOperatorError, setShowOperatorError] = useState(false);
    const [showOperatorValueError, setShowOperatorValueError] = useState(false);
    const [operatorErrorMessage, setOperatorErrorMessage] = useState("");
    const [operatorValueErrorMessage, setOperatorValueErrorMessage] = useState("");
    const [showRangeError, setShowRangeError] = useState(false); //case_3
    const [rangeErrorMessage, setRangeErrorMessage] = useState({
        min: "",
        max: ""
    })
    const { displayName, placeholderText } = displayAttributes;
    const { validation: { mandatoryField }, selectedValues: prevSelectedValues, selectedOperator } = valueConfig;
    const { sectionId, formSubmitClick, viewMode } = additionalProps || {}
    const [selectedOperatorObj, setSelectedOperatorObj] = useState(selectedOperator ?
        {
            id: selectedOperator,
            item: selectedOperator.toUpperCase()
        } : null);

    const errorConfig = {
        mandatoryField: "Field cannot be empty"
    }

    const caseSets = {
        CASE_1: "CASE_1",
        CASE_2: "CASE_2",
        CASE_3: "CASE_3"
    }

    const operatorSet1 = ["EQUAL", "GREATER_THAN_EQUAL", "LESS_THAN_EQUAL"]
    const operatorSet2 = ["INCLUDES", "EXCLUDES"] // show the add button in case 2 only
    const operatorSet3 = ["BETWEEN"]

    const ref = useRef()
    const dropdownButtonRef = useRef()
    const dropdownListRef = useRef()
    useOutsideClick(ref, () => {
        setDropdownOpen(false)
    })

    useEffect(() => {
        if (prevSelectedValues) {
            if (operatorSet1.includes(selectedOperatorObj?.item) || operatorSet2.includes(selectedOperatorObj?.item)) {
                setOperatorValue(prevSelectedValues)
            } else if (operatorSet3.includes(selectedOperatorObj?.item)) {
                // Handle BETWEEN operator's array of values
                if (Array.isArray(prevSelectedValues) && prevSelectedValues.length > 0) {
                    // First value is min
                    if (prevSelectedValues[0]) {
                        setMinValue(Number(prevSelectedValues[0]))
                    }
                    // Second value is max
                    if (prevSelectedValues.length > 1 && prevSelectedValues[1]) {
                        setMaxValue(Number(prevSelectedValues[1]))
                    }
                }
            }
        }
    }, [prevSelectedValues])

    useEffect(() => {
        //validate the input value and show error, and also send back error true to parent via a callback
        if (formSubmitClick) {
            if (mandatoryField) {
                //check for operator
                /**
                 * Here 2 conditions groups are there: case1, case2 and case3
                 * 
                 */

                if (!operatorValue) {
                    setShowOperatorError(true)
                    onErrorCallback(true)
                    setOperatorErrorMessage(errorConfig.mandatoryField)
                } else {
                    setShowOperatorError(false)
                    onErrorCallback(false)
                    setOperatorErrorMessage("")
                }

                if (activeSet === caseSets.CASE_1 || activeSet === caseSets.CASE_2) {
                    if (!operatorValue) {
                        setShowOperatorValueError(true)
                        onErrorCallback(true)
                        setOperatorValueErrorMessage(errorConfig.mandatoryField)
                    } else {
                        setShowOperatorValueError(false)
                        onErrorCallback(false)
                        setOperatorValueErrorMessage("")
                    }
                } else {
                    if (!minValue || !maxValue) {
                        setShowRangeError(true)
                        onErrorCallback(false)
                        if (!minValue) {
                            setRangeErrorMessage(prev => ({
                                ...prev,
                                min: errorConfig.mandatoryField,
                            }))
                        }
                        if (!maxValue) {
                            setRangeErrorMessage(prev => ({
                                ...prev,
                                max: errorConfig.mandatoryField,
                            }))
                        }
                    }
                }
            }
        }

    }, [formSubmitClick])

    const getTransformedPayload = useDebounce((selectedValue) => {
        // In all cases the selected value needs to be passed as a list 
        // In case of case 1 and 2 selectedValue argument to this method is already a list
        // In case of case 3 the selectedValue argument to this method is a colon separated string
        const payloadValues = Array.isArray(selectedValue) ? selectedValue : (
            activeSet === caseSets.CASE_2 ? selectedValue : [selectedValue]
        );
        const payload = {
            fieldType,
            valueConfig: {
                ...valueConfig,
                selectedValues: payloadValues,
                selectedOperator: selectedOperatorObj?.id
            },
            displayAttributes
        }

        onChangeCallback(payload)
    }, 300)

    useEffect(() => {
        //if possible operators have values from one of set 1, 2 or 3
        if (operatorSet1.includes(selectedOperatorObj?.item)) {
            setActiveSet(caseSets.CASE_1)
        } else if (operatorSet2.includes(selectedOperatorObj?.item)) {
            setActiveSet(caseSets.CASE_2)
        } else if (operatorSet3.includes(selectedOperatorObj?.item)) {
            setActiveSet(caseSets.CASE_3)
            // When BETWEEN is selected and both min and max are set, update with both values as array elements
            if (minValue && maxValue) {
                getTransformedPayload([minValue, maxValue]);
            }
        }
    }, [fieldType, valueConfig, displayAttributes, operators, selectedOperatorObj])

    return (
        <div className="flex flex-col gap-1">
            <p className="text-lg">{displayName}
                {mandatoryField && <span className="text-base font-semibold text-red-400">*</span>}
            </p>
            <div className="w-full flex gap-3">
                <div className="flex flex-col gap-1">
                    <div className={`dropdown w-max ${dropdownPosition === 'top' ? "dropdown-top" : ""} ${dropdownOpen ? "dropdown-open" : ""}`} ref={ref}>
                        <div role="button" className="btn m-0 no-animation min-w-72 justify-between bg-accent hover:bg-secondary" ref={dropdownButtonRef} onClick={(evt) => {
                            evt?.stopPropagation()
                            if (!viewMode) {
                                setDropdownOpen(prev => !prev)
                                if (dropdownButtonRef.current && dropdownListRef.current && sectionId) {
                                    const spaceBelow = getPositionFromBotton(sectionId, dropdownButtonRef.current, dropdownListRef.current)
                                    const dropdownHeight = dropdownListRef.current.offsetHeight;
                                    if (spaceBelow < dropdownHeight) {
                                        setDropdownPosition("top")
                                    }
                                }
                            }
                        }}>
                            {selectedOperatorObj?.item ? selectedOperatorObj.item : `Please select a ${displayName}`}
                            <CaretDown className={`font-semibold transition-all duration-100 ${dropdownOpen ? "rotate-180" : ""}`} weight="bold" size={16} />
                        </div>
                        <ul className="dropdown-content my-2 w-full z-[1] menu p-2 flex-col flex-nowrap border border-gray-300 shadow bg-base-100 rounded-box max-h-44 overflow-auto" ref={dropdownListRef}>
                            {
                                operators?.map(
                                    ({ item, id, ..._keys }, index) =>
                                        <li key={id} onClick={(evt) => {
                                            evt?.stopPropagation()
                                            setSelectedOperatorObj({ item, id, ..._keys })
                                            setSelectedValue([])
                                            setMinValue("")
                                            setMaxValue("")
                                            setOperatorValue("")
                                            setDropdownOpen(false)
                                        }}
                                            className="hover:bg-accent rounded-md">
                                            <a>{item}</a>
                                        </li>
                                )
                            }
                        </ul>
                    </div>
                    {
                        showOperatorError && <p className="text-base text-red-400">{operatorErrorMessage}</p>
                    }
                </div>
                {/* Case 1 and Case 2 Single Input  */}
                {
                    activeSet === caseSets.CASE_1 || activeSet === caseSets.CASE_2 ?
                        <>
                            <div className="flex flex-col gap-1">
                                <label className="input input-bordered flex items-center gap-2">
                                    {
                                        viewMode ? <p className="text-sm text-secondary">{operatorValue.length <= 1 ? operatorValue : operatorValue.join(", ")}</p>
                                            :
                                            <>
                                                <input type="text" className="grow" value={operatorValue} placeholder={placeholderText} onChange={(evt) => {
                                                    setOperatorValue(evt.target.value)
                                                    // Trigger onChange for case 1 and case 3, for case 2, clicking on Add will handle it
                                                    if (activeSet === caseSets.CASE_1 || activeSet === caseSets.CASE_3) {
                                                        getTransformedPayload(evt.target.value)
                                                    }
                                                }} />

                                                {
                                                    activeSet === caseSets.CASE_2 &&
                                                    <div className="p-1 z-[1] bg-primary text-accent rounded-md cursor-pointer" onClick={(evt) => {
                                                        evt?.stopPropagation()
                                                        const ogSelectedValue = [...selectedValue]
                                                        const valueToAdd = operatorValue.trim();
                                                        if (valueToAdd) {
                                                            ogSelectedValue.push(valueToAdd)
                                                        }
                                                        const uniqueValues = new Set(ogSelectedValue)
                                                        setSelectedValue(Array.from(uniqueValues))
                                                        setOperatorValue("")
                                                        getTransformedPayload(Array.from(uniqueValues))
                                                    }}>
                                                        Add
                                                    </div>
                                                }
                                            </>
                                    }
                                </label>
                                {
                                    !viewMode && showOperatorValueError && <p className="text-base text-red-400">{operatorValueErrorMessage}</p>
                                }
                            </div>
                        </>
                        :
                        <>
                            <div className="flex flex-col gap-1">
                                <label className="input input-bordered flex items-center gap-2">
                                    {
                                        viewMode ? <p className="text-sm text-secondary">Minimum: {minValue}</p>
                                            :
                                            <>
                                                <input type="number" min={0} value={minValue} className="grow" placeholder="Minimum value" onChange={(evt) => {
                                                    evt?.stopPropagation()
                                                    const value = Number(evt.target.value);
                                                    setMinValue(value)
                                                    // Store min and max as float values in the array
                                                    if (maxValue) {
                                                        getTransformedPayload([value, maxValue])
                                                    } else {
                                                        getTransformedPayload([value])
                                                    }
                                                }} />
                                            </>
                                    }

                                </label>
                                {
                                    !viewMode && showRangeError && <p className="text-base text-red-400">{rangeErrorMessage.min}</p>
                                }
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="input input-bordered flex items-center gap-2">
                                    {
                                        viewMode ? <p className="text-sm text-secondary">Maximum: {maxValue}</p>
                                            :
                                            <>
                                                <input type="number" min={0} value={maxValue} className="grow" placeholder="Maximum value" onChange={(evt) => {
                                                    evt?.stopPropagation();
                                                    const value = Number(evt.target.value);
                                                    setMaxValue(value)
                                                    // Store min and max as float values in the array
                                                    if (minValue) {
                                                        getTransformedPayload([minValue, value])
                                                    } else {
                                                        getTransformedPayload([value])
                                                    }
                                                }} />
                                            </>
                                    }

                                </label>
                                {
                                    !viewMode && showRangeError && <p className="text-base text-red-400">{rangeErrorMessage.max}</p>
                                }
                            </div>
                        </>
                }
            </div>
            {
                selectedValue && selectedValue.length && !operatorSet3.includes(selectedOperatorObj?.id) ?
                    <div className="flex gap-2 flex-wrap mt-2">
                        {
                            selectedValue.map(value => (
                                <div className="badge badge-outline badge-lg bg-secondary p-4 gap-2">
                                    {value}
                                    <X size={15} className="cursor-pointer"
                                        onClick={(evt) => {
                                            evt?.stopPropagation()
                                            const ogSelectedValue = [...selectedValue]
                                            const filteredValue = ogSelectedValue.filter(ogValue => ogValue !== value)
                                            setSelectedValue(filteredValue)
                                            getTransformedPayload(Array.from(filteredValue))
                                        }}
                                    />
                                </div>
                            ))
                        }
                    </div>
                    :
                    null
            }
        </div >
    )
}

const DatePicker = ({
    fieldType,
    valueConfig,
    displayAttributes,
    operators,
    elementId,
    onChangeCallback,
    onErrorCallback,
    ...additionalProps
}) => {
    const [selectedValues, setSelectedValues] = useState("");
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [minDate, setMinDate] = useState(new Date().toJSON().split('T')[0]);
    const { displayName, placeholderText } = displayAttributes;
    const { possibleValues, validation: { mandatoryField }, selectedValues: prevSelectedValues } = valueConfig;
    const { sectionId, formSubmitClick, viewMode, parentConfig } = additionalProps || {};
    const errorConfig = {
        mandatoryField: "Field cannot be empty",
        dateValidation: "End date must be greater than or equal to start date"
    }

    // Determine if this is a start or end date field based on keyName or displayName
    const isStartDate = elementId?.includes("start-date") || displayName?.includes("Start Date");
    const isEndDate = elementId?.includes("end-date") || displayName?.includes("End Date");

    // Find sibling date field if we're in a validity window
    const findSiblingDateField = (siblingType) => {
        if (!parentConfig || !parentConfig.children) return null;
        
        return parentConfig.children.find(child => {
            const childDisplayName = child.displayAttributes?.displayName || "";
            return childDisplayName.includes(siblingType === "start" ? "Start Date" : "End Date");
        });
    }

    useEffect(() => {
        if (prevSelectedValues) {
            setSelectedValues(prevSelectedValues[0])
        }
    }, [prevSelectedValues])

    // Update min date for end date fields whenever parent config changes
    useEffect(() => {
        if (isEndDate && parentConfig) {
            const startDateField = findSiblingDateField("start");
            if (startDateField && startDateField.valueConfig.selectedValues?.[0]) {
                const startDate = startDateField.valueConfig.selectedValues[0];
                setMinDate(startDate);
                
                // If current end date is now invalid, update it to the start date
                if (selectedValues && new Date(selectedValues) < new Date(startDate)) {
                    setSelectedValues(startDate);
                    // Also update parent component with the corrected value
                    const payload = {
                        fieldType,
                        valueConfig: {
                            ...valueConfig,
                            selectedValues: [startDate]
                        },
                        displayAttributes
                    };
                    onChangeCallback(payload);
                }
            }
        } else {
            // Default min date for start date fields
            setMinDate(new Date().toJSON().split('T')[0]);
        }
    }, [parentConfig, isEndDate]);

    // Listen for changes in the parent config's start date (for end date fields)
    useEffect(() => {
        if (isEndDate && parentConfig) {
            const checkStartDate = () => {
                const startDateField = findSiblingDateField("start");
                if (startDateField && startDateField.valueConfig.selectedValues?.[0]) {
                    const startDate = startDateField.valueConfig.selectedValues[0];
                    setMinDate(startDate);
                    
                    // Validate current end date against new start date
                    if (selectedValues && new Date(selectedValues) < new Date(startDate)) {
                        // Auto-correct the end date to match the start date
                        setSelectedValues(startDate);
                        const payload = {
                            fieldType,
                            valueConfig: {
                                ...valueConfig,
                                selectedValues: [startDate]
                            },
                            displayAttributes
                        };
                        onChangeCallback(payload);
                    }
                }
            };
            
            // Check immediately
            checkStartDate();
            
            // Set up an interval to periodically check for changes
            const intervalId = setInterval(checkStartDate, 500);
            
            return () => clearInterval(intervalId);
        }
    }, [parentConfig, isEndDate, selectedValues]);

    useEffect(() => {
        if (formSubmitClick) {
            if (mandatoryField) {
                if (!selectedValues) {
                    onErrorCallback(true)
                    setShowError(true)
                    setErrorMessage(errorConfig.mandatoryField)
                    return;
                }
            }
            
            if (isEndDate && parentConfig) {
                const startDateField = findSiblingDateField("start");
                if (startDateField && startDateField.valueConfig.selectedValues?.[0]) {
                    const startDate = new Date(startDateField.valueConfig.selectedValues[0]);
                    const endDate = new Date(selectedValues);
                    
                    if (endDate < startDate) {
                        // Auto-correct instead of showing error
                        const startDateString = startDateField.valueConfig.selectedValues[0];
                        setSelectedValues(startDateString);
                        const payload = {
                            fieldType,
                            valueConfig: {
                                ...valueConfig,
                                selectedValues: [startDateString]
                            },
                            displayAttributes
                        };
                        onChangeCallback(payload);
                    }
                }
            }
            
            onErrorCallback(false);
            setShowError(false);
            setErrorMessage("");
        }
    }, [formSubmitClick, selectedValues]);

    const getTransformedPayload = useDebounce((selectedValue) => {
        const payload = {
            fieldType,
            valueConfig: {
                ...valueConfig,
                selectedValues: [selectedValue]
            },
            displayAttributes
        }

        onChangeCallback(payload);
        
        // If this is a start date, trigger update of sibling end date
        if (isStartDate && parentConfig) {
            const endDateField = findSiblingDateField("end");
            if (endDateField && endDateField.valueConfig.selectedValues?.[0]) {
                const endDate = new Date(endDateField.valueConfig.selectedValues[0]);
                if (endDate < new Date(selectedValue)) {
                    // Update the end date to match the start date
                    const updatedPayload = {
                        fieldType: endDateField.fieldType,
                        valueConfig: {
                            ...endDateField.valueConfig,
                            selectedValues: [selectedValue]
                        },
                        displayAttributes: endDateField.displayAttributes
                    };
                    // Use a timeout to ensure this happens after the current state update
                    setTimeout(() => {
                        onChangeCallback(updatedPayload);
                    }, 0);
                }
            }
        }
    }, 300);

    return (
        <div className="flex flex-col gap-1">
            <p className="text-lg">{displayName}
                {mandatoryField && <span className="text-base font-semibold text-red-400">*</span>}
            </p>
            {
                viewMode ? <p className="text-sm text-secondary">{selectedValues}</p>
                    :
                    <>
                        <label className="input input-bordered flex items-center gap-2 w-max">
                            <input 
                                type="date" 
                                min={minDate}
                                value={selectedValues} 
                                className="grow" 
                                onChange={(evt) => {
                                    evt?.stopPropagation()
                                    const value = evt.target.value;
                                    
                                    // For end date, validate against start date
                                    if (isEndDate && parentConfig) {
                                        const startDateField = findSiblingDateField("start");
                                        if (startDateField && startDateField.valueConfig.selectedValues?.[0]) {
                                            const startDate = new Date(startDateField.valueConfig.selectedValues[0]);
                                            const newEndDate = new Date(value);
                                            
                                            // If the selected end date is before the start date,
                                            // automatically set it to the start date
                                            if (newEndDate < startDate) {
                                                const correctedDate = startDateField.valueConfig.selectedValues[0];
                                                setSelectedValues(correctedDate);
                                                getTransformedPayload(correctedDate);
                                                return;
                                            }
                                        }
                                    }
                                    
                                    setSelectedValues(value);
                                    getTransformedPayload(value);
                                }} 
                            />
                        </label>
                        {
                            showError && <p className="text-base text-red-400">{errorMessage}</p>
                        }
                    </>
            }
        </div>
    )
}

const TimePicker = ({
    fieldType,
    valueConfig,
    displayAttributes,
    operators,
    elementId,
    onChangeCallback,
    onErrorCallback,
    ...additionalProps
}) => {
    const [selectedValues, setSelectedValues] = useState("");
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [minTime, setMinTime] = useState("00:00");
    const { displayName, placeholderText } = displayAttributes;
    const { possibleValues, validation: { mandatoryField }, selectedValues: prevSelectedValues } = valueConfig;
    const { sectionId, formSubmitClick, viewMode, parentConfig } = additionalProps || {};
    const errorConfig = {
        mandatoryField: "Field cannot be empty",
        timeValidation: "End time must be greater than start time for same day"
    }

    // Determine if this is a start or end time field
    const isStartTime = elementId?.includes("start-time") || displayName?.includes("Start Time");
    const isEndTime = elementId?.includes("end-time") || displayName?.includes("End Time");

    // Convert 12-hour format to 24-hour format for comparison
    const convertTo24Hour = (timeStr) => {
        if (!timeStr) return "";
        return timeStr; // Time is already in 24-hour format with HTML time input
    };

    // Find sibling fields in validity window
    const findSiblingField = (siblingType, fieldType) => {
        if (!parentConfig || !parentConfig.children) return null;
        
        return parentConfig.children.find(child => {
            const childDisplayName = child.displayAttributes?.displayName || "";
            const isCorrectType = child.fieldType === fieldType;
            const isCorrectName = childDisplayName.includes(siblingType === "start" ? "Start" : "End");
            return isCorrectType && isCorrectName;
        });
    };

    useEffect(() => {
        if (prevSelectedValues) {
            setSelectedValues(prevSelectedValues[0])
        }
    }, [prevSelectedValues])

    // Check if dates are the same day
    const areDatesTheSameDay = () => {
        if (!parentConfig) return false;
        
        const startDateField = findSiblingField("start", "DATE_PICKER");
        const endDateField = findSiblingField("end", "DATE_PICKER");
        
        if (startDateField?.valueConfig?.selectedValues?.[0] && 
            endDateField?.valueConfig?.selectedValues?.[0]) {
            return startDateField.valueConfig.selectedValues[0] === endDateField.valueConfig.selectedValues[0];
        }
        
        return false;
    };

    // Compare two time strings (in 24-hour format)
    const isTimeAfter = (time1, time2) => {
        if (!time1 || !time2) return true;
        
        // Convert to comparable values (minutes since midnight)
        const [hours1, minutes1] = time1.split(":").map(Number);
        const [hours2, minutes2] = time2.split(":").map(Number);
        
        const totalMinutes1 = hours1 * 60 + minutes1;
        const totalMinutes2 = hours2 * 60 + minutes2;
        
        return totalMinutes1 >= totalMinutes2;
    };

    // Update min time for end time fields when start time changes on same day
    useEffect(() => {
        if (isEndTime && parentConfig) {
            const updateMinTime = () => {
                const startTimeField = findSiblingField("start", "TIME_PICKER");
                
                // Check if dates are the same
                if (areDatesTheSameDay() && startTimeField?.valueConfig?.selectedValues?.[0]) {
                    const startTime = startTimeField.valueConfig.selectedValues[0];
                    setMinTime(startTime);
                    
                    // Validate current end time against new min time
                    if (selectedValues && !isTimeAfter(selectedValues, startTime)) {
                        setShowError(true);
                        setErrorMessage(errorConfig.timeValidation);
                        onErrorCallback(true);
                    } else if (selectedValues) {
                        setShowError(false);
                        setErrorMessage("");
                        onErrorCallback(false);
                    }
                } else {
                    // If dates are different, reset min time
                    setMinTime("00:00");
                    if (showError) {
                        setShowError(false);
                        setErrorMessage("");
                        onErrorCallback(false);
                    }
                }
            };
            
            // Check immediately
            updateMinTime();
            
            // Set up an interval to periodically check for changes
            const intervalId = setInterval(updateMinTime, 500);
            
            return () => clearInterval(intervalId);
        }
    }, [parentConfig, isEndTime, selectedValues]);

    useEffect(() => {
        // Validate the input value and show error, and also send back error true to parent via a callback
        if (formSubmitClick) {
            if (mandatoryField) {
                // Check if the field has been selected or not
                if (!selectedValues) {
                    onErrorCallback(true);
                    setShowError(true);
                    setErrorMessage(errorConfig.mandatoryField);
                    return;
                }
            }
            
            // For end time on same day, validate against start time
            if (isEndTime && parentConfig && areDatesTheSameDay()) {
                const startTimeField = findSiblingField("start", "TIME_PICKER");
                if (startTimeField?.valueConfig?.selectedValues?.[0]) {
                    const startTime = startTimeField.valueConfig.selectedValues[0];
                    
                    if (!isTimeAfter(selectedValues, startTime)) {
                        onErrorCallback(true);
                        setShowError(true);
                        setErrorMessage(errorConfig.timeValidation);
                        return;
                    }
                }
            }
            
            // If we reach here, no errors found
            onErrorCallback(false);
            setShowError(false);
            setErrorMessage("");
        }
    }, [formSubmitClick, selectedValues]);

    const getTransformedPayload = useDebounce((selectedValue) => {
        const payload = {
            fieldType,
            valueConfig: {
                ...valueConfig,
                selectedValues: [selectedValue]
            },
            displayAttributes
        }

        onChangeCallback(payload);
        
        // If this is a start time and dates are the same, trigger validation of end time
        if (isStartTime && parentConfig && areDatesTheSameDay()) {
            const endTimeField = findSiblingField("end", "TIME_PICKER");
            if (endTimeField?.valueConfig?.selectedValues?.[0]) {
                const endTime = endTimeField.valueConfig.selectedValues[0];
                // Removed empty conditional that wasn't performing any operation
            }
        }
    }, 300);

    return (
        <div className="flex flex-col gap-1">
            <p className="text-lg">{displayName}
                {mandatoryField && <span className="text-base font-semibold text-red-400">*</span>}
            </p>
            {
                viewMode ? <p className="text-sm text-secondary">{selectedValues}</p>
                    :
                    <>
                        <label className="input input-bordered flex items-center gap-2 w-max">
                            <input 
                                type="time" 
                                className="grow" 
                                value={selectedValues} 
                                min={isEndTime && areDatesTheSameDay() ? minTime : undefined}
                                onChange={(evt) => {
                                    evt?.stopPropagation();
                                    const value = evt.target.value;
                                    
                                    // For end time on same day, validate against start time
                                    if (isEndTime && parentConfig && areDatesTheSameDay()) {
                                        const startTimeField = findSiblingField("start", "TIME_PICKER");
                                        if (startTimeField?.valueConfig?.selectedValues?.[0]) {
                                            const startTime = startTimeField.valueConfig.selectedValues[0];
                                            
                                            if (!isTimeAfter(value, startTime)) {
                                                setShowError(true);
                                                setErrorMessage(errorConfig.timeValidation);
                                                onErrorCallback(true);
                                            } else {
                                                setShowError(false);
                                                setErrorMessage("");
                                                onErrorCallback(false);
                                            }
                                        }
                                    }
                                    
                                    setSelectedValues(value);
                                    getTransformedPayload(value);
                                }} 
                            />
                        </label>
                        {
                            showError && <p className="text-base text-red-400">{errorMessage}</p>
                        }
                    </>
            }
        </div>
    )
}

const FIELD_TYPES = {
    "SINGLE_SELECT_DROPDOWN": "SINGLE_SELECT_DROPDOWN",
    "TEXT_AREA": "TEXT_AREA",
    "MULTI_SELECT_DROPDOWN": "MULTI_SELECT_DROPDOWN",
    "MULTI_SELECT_CHECKBOX": "MULTI_SELECT_CHECKBOX",
    "TEXT_BOX": "TEXT_BOX",
    "DATE_PICKER": "DATE_PICKER",
    "TIME_PICKER": "TIME_PICKER",
    "TEXT_INPUT": "TEXT_INPUT"
}

/**
 * @typedef FieldProps
 * @property {keyof FIELD_TYPES} fieldType Describes the type of field to display
 * @property {Object} valueConfig Describes the config to fill field value
 * @property {Object} displayAttributes Desribes the field level meta data
 * @property {Function} onChangeCallback On Field value change invokation callback
 * @property {String} className Additional classes to be sent for styling
 */

/**
 * 
 * @param {FieldProps} props Props passed to Fields component
 * @returns {React.FC}
 */
export const Fields = ({ fieldType, valueConfig, displayAttributes, onChangeCallback, onErrorCallback, className, ...additionalProps }) => {

    switch (fieldType) {
        case FIELD_TYPES.SINGLE_SELECT_DROPDOWN:
            return (
                <SingleSelectDropdown
                    fieldType={fieldType}
                    valueConfig={valueConfig}
                    displayAttributes={displayAttributes}
                    onChangeCallback={onChangeCallback}
                    onErrorCallback={onErrorCallback}
                    {...additionalProps}
                />
            )
        case FIELD_TYPES.MULTI_SELECT_DROPDOWN:
            return (
                <MultiSelectDropdown
                    fieldType={fieldType}
                    valueConfig={valueConfig}
                    displayAttributes={displayAttributes}
                    onChangeCallback={onChangeCallback}
                    onErrorCallback={onErrorCallback}
                    {...additionalProps}
                />
            )
        case FIELD_TYPES.MULTI_SELECT_CHECKBOX:
            return (
                <MultiSelectCheckBox
                    fieldType={fieldType}
                    valueConfig={valueConfig}
                    displayAttributes={displayAttributes}
                    onChangeCallback={onChangeCallback}
                    onErrorCallback={onErrorCallback}
                    {...additionalProps}
                />
            )
        case FIELD_TYPES.DATE_PICKER:
            return (
                <DatePicker
                    fieldType={fieldType}
                    valueConfig={valueConfig}
                    displayAttributes={displayAttributes}
                    onChangeCallback={onChangeCallback}
                    onErrorCallback={onErrorCallback}
                    {...additionalProps}
                />
            )
        case FIELD_TYPES.TIME_PICKER:
            return (
                <TimePicker
                    fieldType={fieldType}
                    valueConfig={valueConfig}
                    displayAttributes={displayAttributes}
                    onChangeCallback={onChangeCallback}
                    onErrorCallback={onErrorCallback}
                    {...additionalProps}
                />
            )
        case FIELD_TYPES.TEXT_AREA:
            return (
                <TextArea
                    fieldType={fieldType}
                    valueConfig={valueConfig}
                    displayAttributes={displayAttributes}
                    onChangeCallback={onChangeCallback}
                    onErrorCallback={onErrorCallback}
                    {...additionalProps}
                />
            )
        case FIELD_TYPES.TEXT_BOX:
            return (
                <TextBox
                    fieldType={fieldType}
                    valueConfig={valueConfig}
                    displayAttributes={displayAttributes}
                    onChangeCallback={onChangeCallback}
                    onErrorCallback={onErrorCallback}
                    {...additionalProps}
                />
            )
        case FIELD_TYPES.TEXT_INPUT:
            return (
                <TextInput
                    fieldType={fieldType}
                    valueConfig={valueConfig}
                    displayAttributes={displayAttributes}
                    onChangeCallback={onChangeCallback}
                    onErrorCallback={onErrorCallback}
                    {...additionalProps}
                />
            )
        default: return null
    }

}

//TODO: ELEMENT_CONDITION_GROUP